import {
  Component,
  inject,
  input,
  output,
  model,
  effect,
  signal,
  computed,
  untracked,
  ChangeDetectionStrategy,
} from '@angular/core';
import { Router } from '@angular/router';
import { FormsModule, ReactiveFormsModule, FormBuilder, FormGroup } from '@angular/forms';
import { firstValueFrom } from 'rxjs';
import { DialogModule } from 'primeng/dialog';
import { CheckboxModule } from 'primeng/checkbox';
import { ButtonModule } from 'primeng/button';
import { ToastModule } from 'primeng/toast';
import { InputTextModule } from 'primeng/inputtext';
import { InputMaskModule } from 'primeng/inputmask';
import { SelectModule } from 'primeng/select';
import { TooltipModule } from 'primeng/tooltip';
import { MessageService } from 'primeng/api';
import { ActionsSummaryDialogComponent } from '../actions-summary-dialog/actions-summary-dialog.component';
import { DispatchAction, DispatchActionOptions } from '../../models/dispatch-actions.model';
import { DispatchActionsConfig } from '../../models/dispatch-actions-config.model';
import { ActionsSummaryData } from '../../models/dispatch-actions.model';
import { DispatchActionsService } from '../../services/dispatch-actions.service';
import { DispatchLogContext } from '../../services/utils/dispatch-xml-builder.util';
import {
  initializeActions,
  getVisibleActions,
  validateActions,
  handleVendorTransmitChange,
} from '../../services/dispatch-actions-helper.service';
import { toActionsSummaryData } from '../../models/dispatch-actions.model';

/**
 * Dispatch Actions Drawer Component
 * Dialog for executing dispatch actions on assignments
 *
 * Matches legacy InitializeActionsDialog() from dispatch_home.js (lines 5950-6993)
 *
 * Features:
 * - Checkbox-based action selection
 * - User preset values from backend
 * - "Last done" timestamp display
 * - Auto-acknowledge when transmitting to vendor
 * - Non-blocking execution (dialog closes immediately)
 * - Toast notifications for progress/results
 */
@Component({
  selector: 'app-dispatch-actions-drawer',
  imports: [
    FormsModule,
    ReactiveFormsModule,
    DialogModule,
    CheckboxModule,
    ButtonModule,
    ToastModule,
    InputTextModule,
    InputMaskModule,
    SelectModule,
    TooltipModule,
    ActionsSummaryDialogComponent,
  ],
  providers: [MessageService],
  templateUrl: './dispatch-actions-drawer.component.html',
  styleUrl: './dispatch-actions-drawer.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class DispatchActionsDrawerComponent {
  private readonly dispatchActionsService = inject(DispatchActionsService);
  private readonly messageService = inject(MessageService);
  private readonly fb = inject(FormBuilder);
  private readonly router = inject(Router);

  // Two-way binding for dialog visibility
  visible = model<boolean>(false);

  // Configuration input (consolidates all inputs)
  config = input.required<DispatchActionsConfig>();

  // Submit event output
  submit = output<DispatchActionOptions>();

  // Dispatch actions list (using signal for reactivity)
  dispatchActions = signal<DispatchAction[]>([]);

  // Loading state (signal for Angular 20 reactivity)
  isSubmitting = signal<boolean>(false);

  // Actions summary dialog state (signals required for OnPush)
  showActionsSummaryDialog = signal<boolean>(false);
  actionsSummaryData = signal<ActionsSummaryData | null>(null);

  // Form for CarPics
  // Matches legacy rockPicsRequestType select binding from dispatch_home.js (line 6609):
  //   'NA:NA;Original Estimate:ORIGINAL ESTIMATE;Supplemental Appraisal:SUPPLEMENTAL APPRAISAL'
  carPicsForm: FormGroup;
  requestTypeOptions: Array<{ label: string; value: string }> = [
    { label: 'NA', value: 'NA' },
    { label: 'Original Estimate', value: 'ORIGINAL ESTIMATE' },
    { label: 'Supplemental Appraisal', value: 'SUPPLEMENTAL APPRAISAL' },
  ];

  // Computed signal for visible actions (reactive filtering)
  visibleDispatchActions = computed(() => getVisibleActions(this.dispatchActions(), this.config()));

  // Computed signal for formatted unit display
  formattedUnit = computed(() => {
    const currentConfig = this.config();
    const vehicleInfo = currentConfig.vehicleInfo;

    if (vehicleInfo?.vin || vehicleInfo?.year || vehicleInfo?.make) {
      const parts = ['veh'];
      if (vehicleInfo.year) parts.push(vehicleInfo.year);
      if (vehicleInfo.make) parts.push(vehicleInfo.make);
      if (vehicleInfo.model) parts.push(vehicleInfo.model);
      return parts.join('-');
    }

    return currentConfig.unitName ?? 'N/A';
  });

  constructor() {
    // Initialize form — default to 'NA' to match legacy first-option behavior
    this.carPicsForm = this.fb.group({
      requestType: ['NA'],
      cellPhone: [''],
    });

    // Effect to update actions when dialog visibility changes.
    // config() is read via untracked() so config mutations while the drawer is
    // already visible do NOT re-trigger the effect and cause a second submit.
    effect(() => {
      if (this.visible()) {
        const currentConfig = untracked(() => this.config());

        // Defer initialization to next tick to ensure all inputs are updated
        setTimeout(() => {
          this.initializeActions();
          // Seed the form from the user's preset (legacy dispatch_home.js xmlmap on
          // rockPicsRequestType). Fall back to 'NA' — the first option — when the
          // preset is missing, so the dropdown still has a defined value.
          const requestTypePreset = currentConfig.presets?.transmitCarPicsRequestTypePreset;
          this.carPicsForm.reset({
            requestType: this.normalizeRequestType(requestTypePreset),
            cellPhone: currentConfig.contactPhone ?? '',
          });
          // Auto-submit new dispatches (legacy automaticDispatchActions), unless the flow
          // requires a manual confirm (Transfer).
          if (currentConfig.isNewDispatch && !currentConfig.requireConfirmation) {
            this.onSubmit();
          }
        }, 0);
      }
    });
  }

  /**
   * Initialize dispatch actions list
   * Delegates to helper service for business logic
   */
  initializeActions(): void {
    this.dispatchActions.set(initializeActions(this.config()));
  }

  /**
   * Coerce a preset value to one of the dropdown's allowed options.
   * Why: backend presets may arrive in mixed case or as an unknown value;
   *      PrimeNG's p-select would render blank if the value isn't in the option list.
   */
  private normalizeRequestType(preset: string | undefined): string {
    const normalized = preset?.trim().toUpperCase();
    return this.requestTypeOptions.some((o) => o.value === normalized) ? normalized! : 'NA';
  }

  /**
   * Close the drawer
   */
  onClose(): void {
    this.visible.set(false);
  }

  /**
   * Close actions summary dialog
   */
  closeActionsSummary(): void {
    this.showActionsSummaryDialog.set(false);
    this.actionsSummaryData.set(null);
  }

  /**
   * Handle any checkbox change
   * What: Delegates to the helper service to apply business rules (like vendor transmission)
   *       and updates the actions signal with the immutable result.
   * Why: Signal reactivity requires a new array reference to trigger re-evaluation.
   */
  onActionChange(action: DispatchAction): void {
    const updatedActions = handleVendorTransmitChange(this.dispatchActions(), action);
    this.dispatchActions.set(updatedActions);
  }

  /**
   * Build dispatch action options from checkbox states
   * Matches legacy postdata format from DispatchActions()
   * Angular 20 best practice: Log state for debugging and ensure immutability
   */
  private buildActionOptions(): DispatchActionOptions {
    const options: DispatchActionOptions = {};

    this.dispatchActions().forEach((action) => {
      const fieldName = action.fieldName as keyof DispatchActionOptions;
      const value = action.checked ? 'on' : 'off';
      options[fieldName] = value;

      // Include done timestamps
      const doneFieldName = `${action.fieldName}Done` as keyof DispatchActionOptions;
      if (action.doneTimestamp) {
        options[doneFieldName] = action.doneTimestamp;
      }
    });

    // Forward Car/Home Pics request type + cell phone from the form so the
    // backend XML carries the user's selection (legacy dispatch_home.js postdata).
    const carPics = this.carPicsForm.value;
    if (carPics.requestType) {
      options.rockPicsRequestType = carPics.requestType;
    }
    if (carPics.cellPhone) {
      options.rockPicsCellPhone = carPics.cellPhone;
    }

    return options;
  }

  /**
   * Validate before submit
   * Uses helper service and returns promise for async validation
   */
  private validateBeforeSubmit(): { valid: boolean; message?: string } {
    // Skip "at least one action" validation for new dispatches (auto-submit flow)
    if (!this.config().isNewDispatch) {
      const validation = validateActions(this.dispatchActions());
      if (!validation.valid) {
        return validation;
      }
    }

    return { valid: true };
  }

  /**
   * Submit dispatch actions
   * Angular 20 best practice: Using signals for reactive state management
   */
  async onSubmit(): Promise<void> {
    // Guard: prevent duplicate executions (e.g. if effect fires twice or user
    // double-clicks). isSubmitting is set synchronously BEFORE the first await
    // so a second call that arrives in any subsequent macrotask will short-circuit.
    if (this.isSubmitting()) return;
    this.isSubmitting.set(true);

    const validation = this.validateBeforeSubmit();
    if (!validation.valid) {
      this.isSubmitting.set(false); // Release lock on validation failure
      if (validation.message) {
        this.messageService.add({
          severity: 'warn',
          summary: 'Validation Error',
          detail: validation.message,
          life: 4000,
        });
      }
      return;
    }

    // Build options
    const options = this.buildActionOptions();

    // Execute single dispatchSilent or transferSilent API call depending on context
    const currentConfig = this.config();
    const appointmentIdNum =
      typeof currentConfig.appointmentId === 'string'
        ? parseInt(currentConfig.appointmentId)
        : currentConfig.appointmentId;

    // Build activity-log context for transfer so the backend generates descriptions
    // matching the legacy format: "Acknowledged dispatch claimNbr_claimId_dispatchId_unitId_cov_loss on behalf of adjusterName"
    const transferContext: DispatchLogContext = {
      adjusterName: currentConfig.appraiser,
      claimNbr: currentConfig.claimNumber,
      dispatchId: currentConfig.dispatchId,
      unitId: currentConfig.unitId,
      coverageTypeCd: currentConfig.coverageTypeCd,
      reserveLssTypCd: currentConfig.reserveLssTypCd,
    };

    const serviceCall = currentConfig.isNewDispatch
      ? this.dispatchActionsService.performTransferDispatch(
          appointmentIdNum,
          options,
          currentConfig.claimId!,
          currentConfig.transferUserId!,
          currentConfig.unitAsgnmntId,
          transferContext,
        )
      : this.dispatchActionsService.performDispatchActions(appointmentIdNum, options);

    try {
      const result = await firstValueFrom(serviceCall);
      this.isSubmitting.set(false);
      this.visible.set(false);
      this.actionsSummaryData.set(toActionsSummaryData(result));
      this.showActionsSummaryDialog.set(true);
      this.submit.emit(options);
    } catch {
      // Reached only if the observable completes without emitting (e.g. component
      // destroyed mid-flight). Navigate directly — there is no dialog to dismiss.
      this.isSubmitting.set(false);
      this.visible.set(false);
      if (currentConfig.isNewDispatch) {
        this.router.navigate(['/assignments']);
      }
    }
  }
}
